import { QueryFunctionContext } from '@tanstack/react-query';
import { flatMap } from 'lodash';
import { useMemo } from 'react';

import { getJobs, GetJobsParams } from 'api/jobs';
import { useImageAnalysisStatus } from 'components/StudyDashboard/Workflow/useImageAnalysisStatus';
import { JobArchiveState, JobResponse, JobStatus, JobType } from 'interfaces/job';
import { Permission } from 'interfaces/permissionOption';
import { getBatches } from 'utils/batching';
import { getJobsQueryKey, useJobsQuery } from 'utils/queryHooks/jobs/useJobs';
import { useStudyUploadList } from 'utils/queryHooks/useStudyUploadList';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { usePermissions } from 'utils/usePermissions';
import { useQueriesWithErrorAndRetrySnackbar } from 'utils/useQueryWithErrorAndRetrySnackbar';
import {
  countIngestJobStatuses,
  getNewIngestedSlides,
  getNewJobs,
  getNewUploadRows,
  getNewUploadsCutoffMs,
  getTileStateBySlideId,
} from './newUploadsModel';

export const NEW_UPLOADS_JOBS_PAGE_SIZE = 100;
export const NEW_UPLOADS_REFRESH_MS = 15000;
// Keeps the GET /jobs query string, which JSON-encodes the slide IDs into the URL, at a safe length.
export const NEW_UPLOADS_SLIDES_PER_TILE_JOBS_REQUEST = 25;

const getStudyJobsParams = (studyId: string, labId: string, types: JobType[], slideIds?: string[]): GetJobsParams => ({
  filters: { studyId, types, slideIds, archiveState: JobArchiveState.Active },
  labId,
  page: 1,
  pageSize: NEW_UPLOADS_JOBS_PAGE_SIZE,
});

export const useNewUploadsCheck = ({ studyId, openedAt }: { studyId: string; openedAt: number }) => {
  const { labId } = useCurrentLabId();
  const { hasPermission } = usePermissions();
  const canViewJobs = hasPermission(Permission.ViewJobs);
  const cutoffMs = getNewUploadsCutoffMs(openedAt);

  const uploadsQuery = useStudyUploadList({ studyId, refetchIntervalMs: NEW_UPLOADS_REFRESH_MS });
  const newUploads = useMemo(() => getNewUploadRows(uploadsQuery.data, cutoffMs), [uploadsQuery.data, cutoffMs]);
  const newSlides = useMemo(() => getNewIngestedSlides(uploadsQuery.data, cutoffMs), [uploadsQuery.data, cutoffMs]);
  const newSlideIds = useMemo(() => newSlides.map((slide) => slide.slideId), [newSlides]);

  const jobsQueryOptions = {
    enabled: canViewJobs && Boolean(labId),
    refetchInterval: NEW_UPLOADS_REFRESH_MS,
    queriedEntityName: 'job',
    consoleOnly: true,
  };
  const ingestJobsQuery = useJobsQuery(
    getStudyJobsParams(studyId, labId, [JobType.Ingest, JobType.BatchIngest]),
    jobsQueryOptions
  );
  const tileJobBatches = useMemo(
    () => getBatches(newSlideIds, NEW_UPLOADS_SLIDES_PER_TILE_JOBS_REQUEST),
    [newSlideIds]
  );
  // The study can hold more tile jobs than one page, so tile jobs are requested for the new slides only.
  const tileJobQueries = useQueriesWithErrorAndRetrySnackbar<JobResponse>({
    queries: tileJobBatches.map((slideIdBatch) => {
      const jobParams = getStudyJobsParams(studyId, labId, [JobType.TileGeneration], slideIdBatch);
      return {
        queryKey: getJobsQueryKey(jobParams),
        queryFn: ({ signal }: QueryFunctionContext) => getJobs(jobParams, signal),
        ...jobsQueryOptions,
      };
    }),
  });
  const imageAnalysisQuery = useImageAnalysisStatus({
    studyId,
    slideIds: newSlideIds,
    labId,
    enabled: canViewJobs && Boolean(labId) && newSlideIds.length > 0,
  });

  const returnedIngestJobs = ingestJobsQuery.data?.jobs;
  const newIngestJobs = useMemo(() => getNewJobs(returnedIngestJobs, cutoffMs), [returnedIngestJobs, cutoffMs]);
  const newTileJobs = getNewJobs(
    flatMap(tileJobQueries, (query) => query.data?.jobs || []),
    cutoffMs
  );
  // A batch is incomplete when its jobs did not all fit the page, or when it could not be loaded at all.
  const slideIdsWithIncompleteTileEvidence = new Set(
    flatMap(tileJobQueries, (query, index) =>
      !query.data || query.data.totalJobs > query.data.jobs.length ? tileJobBatches[index] : []
    )
  );
  const tileStateBySlideId = getTileStateBySlideId(newTileJobs, newSlideIds, slideIdsWithIncompleteTileEvidence);
  const slidesWithActiveImageAnalysis = useMemo(
    () =>
      new Set(
        (imageAnalysisQuery.data?.statuses || [])
          .filter((status) => status.status === JobStatus.Pending || status.status === JobStatus.Running)
          .map((status) => status.slideId)
      ),
    [imageAnalysisQuery.data]
  );

  const refetch = () =>
    Promise.all([
      uploadsQuery.refetch(),
      canViewJobs ? ingestJobsQuery.refetch() : undefined,
      canViewJobs && newSlideIds.length > 0 ? Promise.all(tileJobQueries.map((query) => query.refetch())) : undefined,
      canViewJobs && newSlideIds.length > 0 ? imageAnalysisQuery.refetch() : undefined,
    ]);

  return {
    canViewJobs,
    cutoffMs,
    newUploads,
    uploadsQuery,
    newIngestJobs,
    ingestJobStatusCounts: countIngestJobStatuses(newIngestJobs),
    // A full page of results that are all new means older new jobs may exist beyond the page.
    isIngestJobListTruncated:
      (returnedIngestJobs?.length || 0) >= NEW_UPLOADS_JOBS_PAGE_SIZE &&
      newIngestJobs.length === returnedIngestJobs?.length,
    ingestJobsQuery,
    newSlides,
    tileStateBySlideId,
    slidesWithActiveImageAnalysis,
    isLoadingSlideJobs: tileJobQueries.some((query) => query.isInitialLoading) || imageAnalysisQuery.isInitialLoading,
    isSlideJobsError: tileJobQueries.some((query) => query.isError) || imageAnalysisQuery.isError,
    isFetching:
      uploadsQuery.isFetching ||
      ingestJobsQuery.isFetching ||
      tileJobQueries.some((query) => query.isFetching) ||
      imageAnalysisQuery.isFetching,
    refetch,
  };
};
